/**
 * Microsoft Entra ID MSAL Configuration (multi-tenant)
 *
 * Supports both Work/School accounts from Microsoft Entra ID organizations
 * and personal Microsoft accounts.
 */

import { Configuration, LogLevel } from '@azure/msal-browser';

// Environment variables from .env files
const ENTRA_CLIENT_ID = process.env.REACT_APP_ENTRA_CLIENT_ID || '';
const ENTRA_AUTHORITY = process.env.REACT_APP_ENTRA_AUTHORITY || 'https://login.microsoftonline.com/common';
const ORGANIZATIONS_AUTHORITY = 'https://login.microsoftonline.com/organizations';
const CONSUMERS_AUTHORITY = 'https://login.microsoftonline.com/consumers';
const REDIRECT_URI = process.env.REACT_APP_ENTRA_REDIRECT_URI || window.location.origin;

/**
 * MSAL Configuration Object
 *
 * Uses /common so the same app registration can accept both Work/School and
 * personal Microsoft accounts. Login requests add a domain hint when the user
 * explicitly chooses an account type.
 */
export const msalConfig: Configuration = {
  auth: {
    clientId: ENTRA_CLIENT_ID,
    authority: ENTRA_AUTHORITY,
    redirectUri: REDIRECT_URI,
    postLogoutRedirectUri: REDIRECT_URI,
  },
  cache: {
    cacheLocation: 'localStorage',
    storeAuthStateInCookie: false,
  },
  system: {
    loggerOptions: {
      loggerCallback: (level: LogLevel, message: string, containsPii: boolean) => {
        if (containsPii) {
          return;
        }
        switch (level) {
          case LogLevel.Error:
            console.error(message);
            break;
          case LogLevel.Warning:
            console.warn(message);
            break;
          case LogLevel.Info:
            console.info(message);
            break;
          default:
            console.log(message);
        }
      },
      logLevel: LogLevel.Warning,
    },
  },
};

/**
 * Shared OIDC scopes for login requests.
 */
const loginScopes = ['openid', 'profile', 'email'];

/**
 * Work/School account login request.
 */
export const organizationLoginRequest = {
  authority: ORGANIZATIONS_AUTHORITY,
  scopes: loginScopes,
  domainHint: 'organizations',
};

/**
 * Personal Microsoft account login request.
 */
export const personalLoginRequest = {
  authority: CONSUMERS_AUTHORITY,
  scopes: loginScopes,
  domainHint: 'consumers',
};

/**
 * Default login request. Marketplace and tenant-linked flows should prefer
 * Work/School accounts.
 */
export const loginRequest = organizationLoginRequest;

/**
 * Generic login request for flows that should show Microsoft's account picker.
 */
export const accountPickerLoginRequest = {
  scopes: loginScopes,
};

/**
 * Scopes for token request (API calls)
 *
 * Uses the same OIDC scopes as login so that acquireTokenSilent can
 * return an ID token whose audience (aud) matches our CLIENT_ID.
 * No custom API scope (Application ID URI) is required in Azure Portal.
 */
export const tokenRequest = {
  scopes: ['openid', 'profile', 'email'],
};

/**
 * Validate MSAL configuration
 */
export function validateMsalConfig(): void {
  if (!ENTRA_CLIENT_ID) {
    throw new Error(
      'REACT_APP_ENTRA_CLIENT_ID is not set. ' +
      'Please add it to your .env file.'
    );
  }

  console.log('MSAL Configuration validated successfully');
}
